import React, {useMemo} from 'react';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';

import {RelativeDate, TimeUnit, TimeUnitLabels} from 'common/widgets/date-range-picker/models/types';
import {LIMITED_ATTRIBUTION_PROJECTS} from 'common/report/constants';
import {useStagedState} from 'common/hooks/useStagedState';
import {withStyles} from 'common/util/react';
import {TooltipReact} from 'common/components/tooltip';
import {SvgIconReact} from 'common/components/svg-icon';
import {InputReact} from 'common/components/input';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {InputSize, InputType, TextAlign, ValidationStatus} from 'common/components/input/types';
import {parseNumber} from 'common/util';
import {SelectReact} from 'common/components/select';
import {ButtonReact} from 'common/components/button';

import {
  DefaultMultiAttributionLookbackDays,
  DefaultMultiAttributionLookbackWindow,
  MaxMultiAttributionLookbackDays,
  MinMultiAttributionLookbackDays,
  MultiAttributionLookbackSessions,
  ReducedMaxMultiAttributionLookbackDays,
} from './constants';
import styles from './index.module.styl';
import css from './index.module.styl?inline';

interface Props {
  elref?: string;
  lookbackWindow: RelativeDate;
  projectId: number;
  onCancel?: () => void;
  onChange?: (lookbackWindow: RelativeDate) => void;
}

export const MultiAttributionLookback = withStyles((props: Props) => {
  const [stagedLookbackWindow, setStagedLookbackWindow] = useStagedState(props.lookbackWindow, props.onChange);

  const maxLookbackDays = LIMITED_ATTRIBUTION_PROJECTS.has(props.projectId.toString())
    ? ReducedMaxMultiAttributionLookbackDays
    : MaxMultiAttributionLookbackDays;
  const isDefaultLookbackWindow = useMemo(
    () => isEqual(stagedLookbackWindow, DefaultMultiAttributionLookbackWindow),
    [stagedLookbackWindow],
  );

  const lookbackUnitSections = useMemo(
    () => [
      {
        items: [TimeUnit.Day, TimeUnit.Session].map((timeUnit) => {
          return {
            label: capitalize(`${TimeUnitLabels[timeUnit]}s`),
            value: timeUnit,
            isSelected: timeUnit === stagedLookbackWindow.unit,
          };
        }),
      },
    ],
    [stagedLookbackWindow.unit],
  );

  const invalidText = useMemo(() => {
    const {unit, value} = stagedLookbackWindow;

    if (unit === TimeUnit.Day) {
      if (!Number.isInteger(value)) {
        return `Lookback days must be an integer value.`;
      }

      if (value < MinMultiAttributionLookbackDays || value > maxLookbackDays) {
        return `Lookback Windows up to ${maxLookbackDays} days are supported. Please choose a value within the range to generate a result.`;
      }
    }

    return ``;
  }, [stagedLookbackWindow, maxLookbackDays]);

  const onChangeUnit = (unit: TimeUnit) => {
    setStagedLookbackWindow({
      unit,
      value: unit === TimeUnit.Session ? MultiAttributionLookbackSessions : DefaultMultiAttributionLookbackDays,
    });
  };

  const onClickReset = () => setStagedLookbackWindow(DefaultMultiAttributionLookbackWindow);

  return (
    <div elref={props.elref} className={styles.content}>
      <div className={styles.title}>
        Lookback Window
        <div className={styles.infoContainer}>
          <SvgIconReact className={styles.infoIcon} icon='info' />
          <TooltipReact maxWidth='250px'>
            The time window where a user's events with this attribution property are counted towards the calculation.
            The window ends when the conversion metric happens.
          </TooltipReact>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.windowSection}>
          <div>
            <InputReact
              className={styles.valueInput}
              disabled={stagedLookbackWindow.unit === TimeUnit.Session}
              size={InputSize.Large}
              type={InputType.Number}
              min={MinMultiAttributionLookbackDays.toString()}
              max={maxLookbackDays.toString()}
              textAlign={TextAlign.Center}
              validationStatus={invalidText ? ValidationStatus.Invalid : ValidationStatus.Default}
              value={stagedLookbackWindow.value !== null ? stagedLookbackWindow.value.toString() : ``}
              onInput={(ev) =>
                setStagedLookbackWindow({
                  ...stagedLookbackWindow,
                  value: parseNumber(ev.target.value),
                })
              }
            />
            {stagedLookbackWindow.unit === TimeUnit.Session && (
              <TooltipReact text='Sessions is currently limited to 1' />
            )}
          </div>
          <SelectReact
            className={styles.unit}
            sections={lookbackUnitSections}
            menuWidth={0}
            size={ButtonSize.Large}
            onChange={(ev) => onChangeUnit(ev.detail.item.value)}
          />
        </div>
        {invalidText && <div className={styles.error}>{invalidText}</div>}
      </div>
      <div className={styles.footer}>
        <ButtonReact
          className={styles.resetButton}
          disabled={isDefaultLookbackWindow}
          theme={ButtonTheme.Tertiary}
          onClick={onClickReset}
        >
          Reset
        </ButtonReact>
        <div className={styles.coreButtons}>
          <ButtonReact theme={ButtonTheme.Tertiary} onClick={props.onCancel}>
            Cancel
          </ButtonReact>
          <ButtonReact
            className={styles.applyButton}
            disabled={!!invalidText}
            theme={ButtonTheme.Primary}
            onClick={() => props.onChange?.(stagedLookbackWindow)}
          >
            Apply
          </ButtonReact>
        </div>
      </div>
    </div>
  );
}, css);
